import React from "react";
import "./HTML_MAIN_CSS.css";

const HTMLResponsive = () => {
  return (
    <div className="html-container">
      <h1>HTML – Responsive Design</h1>

      {/* 1. Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          Responsive web design ensures that web pages render well on different devices and screen sizes, including desktops, tablets, and smartphones.
        </p>
      </section>

      {/* 2. Key Concepts */}
      <section id="key-concepts">
        <h2>2. Key Concepts</h2>
        <ul>
          <li><strong>Fluid Grids:</strong> Layouts using relative units like percentages instead of fixed pixels.</li>
          <li><strong>Flexible Images:</strong> Images that scale with their container using <code>max-width: 100%</code>.</li>
          <li><strong>Media Queries:</strong> CSS rules that apply styles based on device characteristics like width, height, or resolution.</li>
          <li><strong>Viewport Meta Tag:</strong> Controls layout on mobile browsers.</li>
        </ul>
      </section>

      {/* 3. Viewport Meta Tag */}
      <section id="viewport">
        <h2>3. Viewport Meta Tag</h2>
        <pre>
          <code>{`<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>`}</code>
        </pre>
        <p>This ensures that the page scales correctly on mobile devices.</p>
      </section>

      {/* 4. Media Queries */}
      <section id="media-queries">
        <h2>4. Media Queries</h2>
        <p>CSS rules that apply based on screen size or other characteristics:</p>
        <pre>
          <code>{`/* Example: Change background color on smaller screens */
@media (max-width: 768px) {
  body {
    background-color: #f0f0f0;
  }
}`}</code>
        </pre>
      </section>

      {/* 5. Flexible Layouts */}
      <section id="flexible-layouts">
        <h2>5. Flexible Layouts (Flexbox Example)</h2>
        <pre>
          <code>{`<style>
.container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.container > div {
  flex: 1 1 200px; /* grow, shrink, basis */
  padding: 1rem;
  border: 1px solid #ccc;
}
</style>

<div class="container">
  <div>Main Content</div>
  <div>Sidebar</div>
  <div>Advertisement</div>
</div>`}</code>
        </pre>
      </section>

      {/* 6. Flexible Images */}
      <section id="flexible-images">
        <h2>6. Flexible Images</h2>
        <p>Images that scale with the container:</p>
        <pre>
          <code>{`<style>
img {
  max-width: 100%;
  height: auto;
}
</style>

<img src="image.jpg" alt="Responsive example" />`}</code>
        </pre>
      </section>

      {/* 7. Responsive Typography */}
      <section id="typography">
        <h2>7. Responsive Typography</h2>
        <p>Use relative units like <code>em</code>, <code>rem</code>, or percentages instead of pixels:</p>
        <pre>
          <code>{`<style>
h1 {
  font-size: 2rem;
}
p {
  font-size: 1rem;
}
@media (max-width: 600px) {
  h1 {
    font-size: 1.5rem;
  }
  p {
    font-size: 0.875rem;
  }
}
</style>`}</code>
        </pre>
      </section>

      {/* 8. Responsive Navigation */}
      <section id="responsive-nav">
        <h2>8. Responsive Navigation Example</h2>
        <pre>
          <code>{`<nav>
  <ul class="menu">
    <li><a href="#">Home</a></li>
    <li><a href="#">About</a></li>
    <li><a href="#">Contact</a></li>
  </ul>
</nav>

<style>
.menu {
  display: flex;
  list-style: none;
  padding: 0;
}
.menu li {
  margin-right: 1rem;
}
@media (max-width: 600px) {
  .menu {
    flex-direction: column;
  }
  .menu li {
    margin-bottom: 0.5rem;
  }
}
</style>`}</code>
        </pre>
      </section>

      {/* 9. Summary */}
      <section id="summary">
        <h2>9. Summary</h2>
        <ul>
          <li>Responsive design ensures your site works well on all devices.</li>
          <li>Use fluid grids, flexible images, and relative units.</li>
          <li>Use media queries to apply styles for different screen sizes.</li>
          <li>Include the viewport meta tag in the <code>&lt;head&gt;</code>.</li>
          <li>Test layouts on multiple devices and screen sizes.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTMLResponsive;
